import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Rating,
  Avatar,
  Chip,
  Button,
  IconButton,
  Divider,
  Alert,
  LinearProgress,
  Stack,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Container,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Skeleton,
  CircularProgress,
  Snackbar,
  RadioGroup,
  FormControlLabel,
  Radio,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Star as StarIcon,
  Reply as ReplyIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Psychology as AIIcon,
  Send as SendIcon,
  Edit as EditIcon,
  AutoAwesome as MagicIcon,
  QrCode2 as QrCodeIcon,
  Link as LinkIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Email as EmailIcon
} from '@mui/icons-material';

import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';

const ReviewsManager = () => {
  // Generate color based on author name
  const getAuthorColor = (name) => {
    const colors = [
      '#FF6B6B', // Red
      '#4ECDC4', // Teal
      '#45B7D1', // Blue
      '#FFA07A', // Light Salmon
      '#98D8C8', // Mint
      '#F7DC6F', // Yellow
      '#BB8FCE', // Purple
      '#85C1E2', // Sky Blue
      '#F8B88B', // Peach
      '#B4A7D6'  // Lavender
    ];

    if (!name) return colors[0];
    const charCode = name.charCodeAt(0);
    return colors[charCode % colors.length];
  };

  const { locationId: urlLocationId } = useParams();
  const { selectedBusiness } = useBusiness();
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businessData, setBusinessData] = useState(null);

  // Determine effective locationId from selected business or URL
  const effectiveLocationId = selectedBusiness?.locationId || selectedBusiness?.id || selectedBusiness?.googleLocationId || urlLocationId;

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const REVIEWS_PER_PAGE = 10;

  // Reply dialog state
  const [replyDialog, setReplyDialog] = useState({
    open: false,
    review: null,
    text: '',
    isGeneratingAI: false,
    selectedTone: 'professional'
  });

  // Filtering and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [filteredReviews, setFilteredReviews] = useState([]);

  // UI state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [reviewUrlDialogOpen, setReviewUrlDialogOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('d.nawaz@am-beratung.de');
  const [emailMode, setEmailMode] = useState('custom'); // 'custom' or 'default'

  // Fetch business data for review URL and QR code
  const fetchBusinessData = async () => {
    if (effectiveLocationId) {
      try {
        console.log('Fetching business data for locationId:', effectiveLocationId);
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/businesses/${effectiveLocationId}/details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Business data fetched:', data);
          console.log('Has reviewUrl:', !!data.reviewUrl);
          console.log('Has qrCodeUrl:', !!data.qrCodeUrl);
          console.log('All available keys:', Object.keys(data));
          console.log('ReviewUrl value:', data.reviewUrl);
          console.log('QrCodeUrl value:', data.qrCodeUrl);
          setBusinessData(data);
        } else {
          console.error('Failed to fetch business data:', response.status, response.statusText);
        }
      } catch (err) {
        console.error('Error fetching business data:', err);
      }
    }
  };

  useEffect(() => {
    if (effectiveLocationId) {
      console.log('📍 Business changed in ReviewsManager, loading data for:', effectiveLocationId);
      fetchReviews();
      fetchBusinessData();
    }
  }, [effectiveLocationId]);

  useEffect(() => {
    if (selectedBusiness) {
      console.log('Business context updated in ReviewsManager:', {
        businessName: selectedBusiness.businessName,
        locationId: effectiveLocationId
      });
    }
  }, [effectiveLocationId]);

  // Filter & sort + reset page on change
  useEffect(() => {
    if (!reviewsData?.reviews) return;

    let filtered = [...reviewsData.reviews];

    // Search
    if (searchTerm) {
      filtered = filtered.filter(review =>
        (review.text?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        review.author.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Rating filter
    if (filterRating !== 'all') {
      const ratingNum = parseInt(filterRating);
      filtered = filtered.filter(review => review.rating === ratingNum);
    }

    // Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'replied') filtered = filtered.filter(r => r.isReplied);
      if (filterStatus === 'unreplied') filtered = filtered.filter(r => !r.isReplied);
      if (filterStatus === 'new') filtered = filtered.filter(r => r.isNew);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest': return new Date(b.time || 0) - new Date(a.time || 0);
        case 'oldest': return new Date(a.time || 0) - new Date(b.time || 0);
        case 'highest': return b.rating - a.rating;
        case 'lowest': return a.rating - b.rating;
        default: return 0;
      }
    });

    setFilteredReviews(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  }, [reviewsData, searchTerm, filterRating, filterStatus, sortBy]);

  // Pagination logic
  const paginatedReviews = useMemo(() => {
    const start = (currentPage - 1) * REVIEWS_PER_PAGE;
    const end = start + REVIEWS_PER_PAGE;
    return filteredReviews.slice(start, end);
  }, [filteredReviews, currentPage]);

  const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/reviews/${effectiveLocationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      const data = await response.json();
      setReviewsData(data);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      setError(`Fehler beim Laden der Bewertungen: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReplyClick = (review) => {
    setReplyDialog({
      open: true,
      review,
      text: '',
      isGeneratingAI: false,
      selectedTone: 'professional'
    });
  };

  const handleGenerateAIResponse = async () => {
    if (!replyDialog.review) return;

    setReplyDialog(prev => ({ ...prev, isGeneratingAI: true }));

    try {
      const accountId = reviewsData?.location?.accountId;
      if (!accountId) throw new Error('Account ID nicht verfügbar');

      const fullReviewId = replyDialog.review.id;
      const reviewIdMatch = fullReviewId.match(/reviews\/([^\/]+)$/);
      const actualReviewId = reviewIdMatch ? reviewIdMatch[1] : fullReviewId;

      const response = await fetch(`/api/reviews/accounts/${accountId}/locations/${effectiveLocationId}/reviews/${actualReviewId}/generate-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tone: replyDialog.selectedTone,
          businessData: {
            business_name: reviewsData?.location?.name || 'Ihr Unternehmen',
            categories: [{ displayName: 'Unternehmen' }]
          }
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        setReplyDialog(prev => ({ ...prev, text: data.data.generatedResponse, isGeneratingAI: false }));
        setSnackbar({ open: true, message: 'KI-Antwort erfolgreich generiert!', severity: 'success' });
      } else {
        throw new Error(data.error || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Error generating AI response:', error);
      setSnackbar({ open: true, message: `Fehler bei KI-Generierung: ${error.message}`, severity: 'error' });
      setReplyDialog(prev => ({ ...prev, isGeneratingAI: false }));
    }
  };

  const handleReplySubmit = async () => {
    if (!replyDialog.text.trim()) return;

    try {
      console.log('Submitting reply:', replyDialog.review.id, replyDialog.text);

      setSnackbar({ open: true, message: 'Antwort erfolgreich versendet!', severity: 'success' });
      setReplyDialog({ open: false, review: null, text: '', isGeneratingAI: false, selectedTone: 'professional' });
      await fetchReviews();
    } catch (error) {
      setSnackbar({ open: true, message: `Fehler beim Senden: ${error.message}`, severity: 'error' });
    }
  };

  // Handle review URL and QR code functionality
  const handleCopyReviewLink = async () => {
    if (businessData?.reviewUrl) {
      try {
        await navigator.clipboard.writeText(businessData.reviewUrl);
        setSnackbar({ open: true, message: 'Bewertungslink in Zwischenablage kopiert!', severity: 'success' });
      } catch (err) {
        console.error('Failed to copy review URL:', err);
        setSnackbar({ open: true, message: 'Fehler beim Kopieren des Links', severity: 'error' });
      }
    }
  };

  const handleDownloadQR = () => {
    if (businessData?.qrCodeUrl) {
      const link = document.createElement('a');
      link.href = businessData.qrCodeUrl;
      link.download = `qr-code-${businessData.businessName || 'review'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: 'QR-Code heruntergeladen!', severity: 'success' });
    }
  };

  const handleEmailPreview = () => {
    if (!businessData?.reviewUrl || !businessData?.qrCodeUrl) {
      setSnackbar({ open: true, message: 'Bewertungslink oder QR-Code nicht verfügbar', severity: 'error' });
      return;
    }
    // Set default recipient based on mode
    if (emailMode === 'default' && businessData?.email) {
      setEmailRecipient(businessData.email);
    } else {
      setEmailRecipient('d.nawaz@am-beratung.de');
    }
    setEmailPreviewOpen(true);
  };

  const handleEmailModeChange = (event) => {
    const newMode = event.target.value;
    setEmailMode(newMode);
    
    if (newMode === 'default' && businessData?.email) {
      setEmailRecipient(businessData.email);
    } else {
      setEmailRecipient('d.nawaz@am-beratung.de');
    }
  };

  const handleSendEmail = async () => {
    setEmailSending(true);
    
    try {
      const response = await fetch('/api/send-review-promotion-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessData: {
            businessName: businessData.businessName,
            reviewUrl: businessData.reviewUrl,
            qrCodeUrl: businessData.qrCodeUrl
          },
          recipient: emailRecipient
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSnackbar({ open: true, message: `E-Mail erfolgreich an ${emailRecipient} versendet!`, severity: 'success' });
        setEmailPreviewOpen(false);
      } else {
        throw new Error('E-Mail Versand fehlgeschlagen');
      }
    } catch (error) {
      console.error('Email sending error:', error);
      setSnackbar({ open: true, message: `Fehler beim E-Mail Versand: ${error.message}`, severity: 'error' });
    } finally {
      setEmailSending(false);
    }
  };

  const getEmailPreviewContent = () => {
    if (!businessData?.reviewUrl) return '';
    
    return `Guten Tag,

positive Rezensionen sind einer der wichtigsten Faktoren für dein Ranking bei Google Business Profile. Sie signalisieren Google Relevanz und Vertrauenswürdigkeit, wodurch dein Unternehmen in der lokalen Suche weiter oben platziert wird und mehr Sichtbarkeit bei Neukunden erhält.

Damit du künftig schneller und einfacher Feedback einsammeln kannst, haben wir dir die direkten Zugänge vorbereitet. Im Anhang findest du deinen individuellen QR-Code sowie den passenden Direkt-Link.

Unsere Empfehlung für dich:
Um die Anzahl deiner Bewertungen kurzfristig zu steigern, empfehlen wir dir folgende Schritte:

Der direkte Link: Versende den untenstehenden Link per E-Mail oder WhatsApp direkt nach einem erfolgreichen Projektabschluss oder Kauf.

Der QR-Code: Drucke den beigefügten QR-Code auf deine Rechnungen, Lieferscheine oder stelle einen kleinen Aufsteller in deinen Geschäftsräumen auf.

Persönliche Ansprache: Bitte zufriedene Kunden aktiv um einen kurzen Moment Zeit – die Hürde ist durch den Direktzugriff nun minimal, da das Formular sofort öffnet.

Dein Link zum Kopieren:
${businessData.reviewUrl}

Viel Erfolg beim Einsammeln der Rezensionen!

Beste Grüße`;
  };

  const formatRatingDistribution = (distribution) => {
    const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);
    return Object.entries(distribution)
      .reverse()
      .map(([stars, count]) => ({
        stars: parseInt(stars),
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0
      }));
  };

  const getReviewStatus = (rating) => {
    if (rating >= 4.5) return 'excellent';
    if (rating >= 3.5) return 'good';
    if (rating >= 2.5) return 'average';
    return 'poor';
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 2, width: '300px' }} />
          <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 3, width: '200px' }} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" sx={{ fontSize: '1.5rem', mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={60} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <LinearProgress sx={{ borderRadius: 1, height: 6 }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchReviews}>
              <RefreshIcon sx={{ mr: 1 }} />
              Erneut versuchen
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!reviewsData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Keine Bewertungsdaten verfügbar.
        </Alert>
      </Container>
    );
  }

  const { statistics, reviews, location } = reviewsData;
  const ratingStatus = getReviewStatus(statistics.averageRating);

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      {/* Header */}
      <Box sx={{ mb: 0 }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: amDesignColors.primary,
            mb: 1
          }}
        >
          Reputation
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                mb: 0.5,
                fontWeight: 800,
                letterSpacing: '-0.025em',
                color: '#000000',
                fontFamily: amDesignTypography.fontFamily.heading,
                fontSize: '32px'
              }}
            >
              Bewertungen
            </Typography>
           


            <Typography
              variant="body2"
              sx={{
                color: amDesignColors.text.secondary,
                fontFamily: amDesignTypography.fontFamily.body
              }}
            >
              {selectedBusiness?.businessName || 'Ihr Unternehmen'}
            </Typography>
          </Box>
          <Box display="flex" gap={2} >
            {/* Action Buttons - Full Width */}
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={() => {
                console.log('Review button clicked. BusinessData:', businessData);
                setReviewUrlDialogOpen(true);
              }}
               sx={{ borderColor: amDesignColors.text.secondary, color: amDesignColors.text.secondary, borderRadius: 2, px: 3 }}  
            >
              Bewertungslink
            </Button>
            <Button
              variant="outlined"
              startIcon={<QrCodeIcon />}
              onClick={() => {
                console.log('QR button clicked. BusinessData:', businessData);
                setQrDialogOpen(true);
              }}
               sx={{ borderColor: amDesignColors.text.secondary, color: amDesignColors.text.secondary, borderRadius: 2, px: 3 }}  
            >
              QR-Code
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleEmailPreview}
              disabled={!businessData?.reviewUrl || !businessData?.qrCodeUrl}
               sx={{ borderColor: amDesignColors.text.secondary, color: amDesignColors.text.secondary, borderRadius: 2, px: 3 }}  
            >
              E-Mail senden
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchReviews}
               sx={{ borderColor: amDesignColors.text.secondary, color: amDesignColors.text.secondary, borderRadius: 2, px: 3 }}  
            >
              Aktualisieren
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Summary Section */}
      <Paper  sx={{ p: 3, mb: 2, backgroundColor: '#F7F6F3'}}>
        {/* Summary Grid Layout */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Rating Card - Large */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{
              p: 3, textAlign: 'center', backgroundColor: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Typography sx={{ fontWeight: 800, mb: 1, color: '#000000', fontSize: '3rem', lineHeight: 1, fontFamily: 'JetBrains Mono' }}>
                {(statistics.averageRating?.toFixed(1) || '0.0').replace('.', ',')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5, gap: 0.3 }}>
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} sx={{ color: '#FF9500', fontSize: '1.6rem' }} />
                ))}
              </Box>
              <Typography variant="body2" sx={{ color: '#606060', fontWeight: 500, fontSize: '0.85rem', lineHeight: 1.4 }}>
                {statistics.totalReviews || 0} Bewertungen · Top 1 % der Branche
              </Typography>
            </Paper>
          </Grid>

          {/* Response Rate Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{
              p: 3, textAlign: 'center', backgroundColor: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Typography variant="caption" sx={{ color: '#606060', fontWeight: 600, fontSize: '0.8rem', mb: 1, textTransform: 'uppercase' }}>
                Antwortrate
              </Typography>
              <Typography sx={{ fontWeight: 700, color: '#000000', fontSize: '2.2rem', lineHeight: 1, fontFamily: 'JetBrains Mono' }}>
                {statistics.responseRate || 0}%
              </Typography>
            </Paper>
          </Grid>

          {/* Unanswered Card - Highlighted */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{
              p: 3, textAlign: 'center', backgroundColor: '#FBF3E8',
              border: '1px solid #E8D7C3',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Typography variant="caption" sx={{ color: '#B97214', fontWeight: 600, fontSize: '0.8rem', mb: 1, textTransform: 'uppercase' }}>
                Unbeantwortet
              </Typography>
              <Typography sx={{ fontWeight: 700, color: '#B97214', fontSize: '2.2rem', lineHeight: 1, fontFamily: 'JetBrains Mono' }}>
                {statistics.unansweredReviews || 0}
              </Typography>
            </Paper>
          </Grid>

          
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth placeholder="Bewertungen durchsuchen..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              variant="outlined" size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sterne</InputLabel>
              <Select value={filterRating} onChange={(e) => setFilterRating(e.target.value)} label="Sterne">
                <MenuItem value="all">Alle Sterne</MenuItem>
                {[5,4,3,2,1].map(n => <MenuItem key={n} value={n}>{n} Sterne</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md="auto">
            <ToggleButtonGroup
              value={filterStatus}
              exclusive
              onChange={(e, newStatus) => {
                if (newStatus !== null) {
                  setFilterStatus(newStatus);
                }
              }}
              sx={{
                backgroundColor: '#F7F6F3',
                borderRadius: 2,
                border: '1px solid #E0E0E0',
                '& .MuiToggleButton-root': {
                  borderRadius: 2,
                  border: 'none',
                  color: '#606060',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#EFEFEF'
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#FFFFFF',
                    color: '#000000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#FFFFFF'
                    }
                  }
                }
              }}
            >
              <ToggleButton value="all">Alle</ToggleButton>
              <ToggleButton value="unreplied">Offen</ToggleButton>
              <ToggleButton value="replied">Beantwortet</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
             <InputLabel>Sortierung</InputLabel>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sortierung">
                <MenuItem value="newest">Neueste zuerst</MenuItem>
                <MenuItem value="oldest">Älteste zuerst</MenuItem>
                <MenuItem value="highest">Höchste zuerst</MenuItem>
                <MenuItem value="lowest">Niedrigste zuerst</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
        </Grid>
      </Paper>

      {/* Results Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Bewertungen
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zeige {(currentPage - 1) * REVIEWS_PER_PAGE + 1}–{Math.min(currentPage * REVIEWS_PER_PAGE, filteredReviews.length)} von {filteredReviews.length} Ergebnissen
            {searchTerm || filterRating !== 'all' || filterStatus !== 'all' ? ' (gefiltert)' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Reviews List */}
      {filteredReviews.length === 0 ? (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6">Keine Bewertungen gefunden</Typography>
          <Typography variant="body2" color="text.secondary">
            {reviews.length === 0 ? 'Noch keine Bewertungen vorhanden.' : 'Passen Sie Ihre Filter an.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <Stack spacing={3}>
            {paginatedReviews.map((review) => (
              <Fade in key={review.id}>
                <Paper elevation={1} sx={{
                  p: 3, borderRadius: 2,
                  border: '1px solid #E0E0E0',
                  bgcolor: 'white',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
                }}>
                  {/* Header Section */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flex: 1 }}>
                      <Avatar src={review.author.photoUrl} sx={{ width: 48, height: 48, backgroundColor: getAuthorColor(review.author.name), color: 'white', fontWeight: 700, flexShrink: 0 }}>
                        {review.author.name[0]}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#000000' }}>
                            {review.author.name}
                          </Typography>
                          <Rating value={review.rating} readOnly size="small" sx={{ color: '#FF9500' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {review.timeDescription} · {review.time.split('T')[0]}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Status Badge */}
                    {review.isReplied ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.6, backgroundColor: '#4caf5015', borderRadius: '6px', ml: 2 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#4caf50' }}>
                          Beantwortet
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.6, backgroundColor: '#FF950015', borderRadius: '6px', ml: 2 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF9500' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#FF9500' }}>
                          Offen
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Review Text */}
                  {review.text && (
                    <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2.5, color: '#333333' }}>
                      {review.text}
                    </Typography>
                  )}

                  {/* Action Buttons - Only for unanswered reviews */}
                  {!review.isReplied && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ReplyIcon />}
                        onClick={() => handleReplyClick(review)}
                        sx={{
                          bgcolor: '#C41E3A',
                          '&:hover': { bgcolor: '#a01729' },
                          fontWeight: 600,
                          px: 2
                        }}>
                        Antworten
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<MagicIcon />}
                        onClick={() => {
                          handleReplyClick(review);
                          setTimeout(() => handleGenerateAIResponse(), 100);
                        }}
                        sx={{
                          borderColor: '#C41E3A',
                          color: '#C41E3A',
                          fontWeight: 600,
                          px: 2,
                          '&:hover': {
                            bgcolor: 'rgba(196, 30, 58, 0.04)',
                            borderColor: '#C41E3A'
                          }
                        }}>
                        KI-Antwort
                      </Button>
                    </Box>
                  )}

                  {review.reply && (
                    <Box sx={{ ml: 2, p: 3, bgcolor: '#f8fbf8', borderRadius: 2, borderLeft: '4px solid #4caf50' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CheckCircleIcon color="success" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          Antwort vom Unternehmen • {review.reply.timeDescription}
                        </Typography>
                      </Box>
                      <Typography>{review.reply.text}</Typography>
                    </Box>
                  )}
                </Paper>
              </Fade>
            ))}
          </Stack>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
              <Paper elevation calm={1} sx={{ borderRadius: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
                  <Button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                    Zurück
                  </Button>

                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let page;
                    if (totalPages <= 7) page = i + 1;
                    else if (currentPage <= 4) page = i + 1;
                    else if (currentPage > totalPages - 4) page = totalPages - 6 + i;
                    else page = currentPage - 3 + i;

                    if (page < 1 || page > totalPages) return null;

                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "contained" : "text"}
                        onClick={() => handlePageChange(page)}
                        sx={{
                          minWidth: 40,
                          bgcolor: currentPage === page ? '#C41E3A' : 'transparent',
                          color: currentPage === page ? 'white' : 'text.primary',
                          '&:hover': { bgcolor: currentPage === page ? '#a01729' : 'rgba(0,0,0,0.04)' }
                        }}
                      >
                        {page}
                      </Button>
                    );
                  }).filter(Boolean)}

                  <Button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                    Weiter
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}
        </>
      )}

      {/* Reply Dialog */}
      <Dialog open={replyDialog.open} onClose={() => setReplyDialog({ open: false, review: null, text: '', isGeneratingAI: false, selectedTone: 'professional' })}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReplyIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Auf Bewertung antworten</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {replyDialog.review && (
            <>
              <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Avatar src={replyDialog.review.author.photoUrl} sx={{ width: 48, height: 48, backgroundColor: getAuthorColor(replyDialog.review.author.name), color: 'white', fontWeight: 600 }}>
                    {replyDialog.review.author.name[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{replyDialog.review.author.name}</Typography>
                    <Rating value={replyDialog.review.rating} readOnly size="small" />
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>"{replyDialog.review.text}"</Typography>
              </Paper>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>KI-Antwort generieren</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Tonfall</InputLabel>
                    <Select value={replyDialog.selectedTone}
                      onChange={(e) => setReplyDialog(prev => ({ ...prev, selectedTone: e.target.value }))} label="Tonfall">
                      <MenuItem value="professional">Professionell</MenuItem>
                      <MenuItem value="friendly">Freundlich</MenuItem>
                      <MenuItem value="empathetic">Einfühlsam</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="outlined" startIcon={replyDialog.isGeneratingAI ? <CircularProgress size={16} /> : <MagicIcon />}
                    onClick={handleGenerateAIResponse} disabled={replyDialog.isGeneratingAI}
                    sx={{ borderColor: '#C41E3A', color: '#C41E3A' }}>
                    {replyDialog.isGeneratingAI ? 'Generiere...' : 'KI generieren'}
                  </Button>
                </Box>
              </Box>

              <TextField fullWidth multiline rows={6} label="Ihre Antwort" placeholder="Antwort eingeben oder KI generieren..."
                value={replyDialog.text}
                onChange={(e) => setReplyDialog(prev => ({ ...prev, text: e.target.value }))}
                variant="outlined"
              />
              {replyDialog.text && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Zeichen: {replyDialog.text.length} / 500 empfohlen
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setReplyDialog({ open: false, review: null, text: '', isGeneratingAI: false, selectedTone: 'professional' })}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleReplySubmit} disabled={!replyDialog.text.trim()}
            startIcon={<SendIcon />} sx={{ bgcolor: '#C41E3A', '&:hover': { bgcolor: '#a01729' } }}>
            Antwort senden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review URL Dialog */}
      <Dialog open={reviewUrlDialogOpen} onClose={() => setReviewUrlDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon />
            <Typography variant="h6">Bewertungslink teilen</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            {businessData?.reviewUrl ? (
              <>
                <Typography variant="body1" gutterBottom>
                  Teilen Sie diesen Link mit Ihren Kunden, um Bewertungen zu sammeln:
                </Typography>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', color: 'text.secondary' }}>
                    {businessData.reviewUrl}
                  </Typography>
                </Paper>
                <Box display="flex" gap={2} justifyContent="center">
                  <Button 
                    variant="contained" 
                    startIcon={<ShareIcon />}
                    onClick={handleCopyReviewLink}
                  >
                    Link kopieren
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(businessData.reviewUrl, '_blank')}
                  >
                    Link öffnen
                  </Button>
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <LinkIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="body1" color="textSecondary" textAlign="center">
                  Kein Bewertungslink verfügbar für dieses Unternehmen
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewUrlDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <QrCodeIcon />
            <Typography variant="h6">QR-Code für Bewertungslink</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            {businessData?.qrCodeUrl ? (
              <>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                  <img 
                    src={businessData.qrCodeUrl} 
                    alt="QR Code für Bewertungen" 
                    style={{ width: '200px', height: '200px', display: 'block' }}
                  />
                </Paper>
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  Scannen Sie diesen QR-Code, um direkt zur Bewertungsseite zu gelangen
                </Typography>
                <Box display="flex" gap={2}>
                  <Button 
                    variant="outlined" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadQR}
                  >
                    QR-Code herunterladen
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<ShareIcon />}
                    onClick={handleCopyReviewLink}
                  >
                    Link kopieren
                  </Button>
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <QrCodeIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="body1" color="textSecondary" textAlign="center">
                  Kein QR-Code verfügbar für dieses Unternehmen
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={emailPreviewOpen} onClose={() => setEmailPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon />
            <Typography variant="h6">E-Mail Vorschau</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Email Mode Selection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                E-Mail Modus
              </Typography>
              <RadioGroup
                value={emailMode}
                onChange={handleEmailModeChange}
                row
              >
                <FormControlLabel 
                  value="custom" 
                  control={<Radio />} 
                  label="Benutzerdefiniert"
                />
                <FormControlLabel 
                  value="default" 
                  control={<Radio />} 
                  label={`An Unternehmen${businessData?.email ? ` (${businessData.email})` : ' (keine E-Mail hinterlegt)'}`}
                  disabled={!businessData?.email}
                />
              </RadioGroup>
            </Box>

            {/* Recipient Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Empfänger
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder={emailMode === 'custom' ? 'E-Mail Adresse eingeben' : 'Unternehmens E-Mail Adresse'}
                type="email"
                disabled={emailMode === 'default'}
                helperText={emailMode === 'default' ? 'E-Mail wird an die im Unternehmensprofil hinterlegte Adresse gesendet' : 'Geben Sie eine benutzerdefinierte E-Mail Adresse ein'}
              />
            </Box>

            {/* Subject Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Betreff
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  Mehr Sichtbarkeit – Google-Bewertungen einfach sammeln!
                </Typography>
              </Paper>
            </Box>

            {/* Email Content Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                E-Mail Inhalt
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                  {getEmailPreviewContent()}
                </Typography>
              </Paper>
            </Box>

            {/* Attachment Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Anhang
              </Typography>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <img 
                  src={businessData?.qrCodeUrl} 
                  alt="QR Code Anhang" 
                  style={{ width: '60px', height: '60px', borderRadius: '4px' }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    qr-code-{businessData?.businessName || 'bewertung'}.png
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    QR-Code für direkten Bewertungslink
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEmailPreviewOpen(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSendEmail}
            disabled={emailSending || !emailRecipient.trim()}
            startIcon={emailSending ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}
          >
            {emailSending ? 'Sende...' : `E-Mail an ${emailRecipient} senden`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReviewsManager;